import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { scrollState } from '../state/scroll';
import { SECTIONS, PROFILE } from '../data/content';

const N = SECTIONS.length;

// Condensed bottom-bar links (each jumps to a representative section).
const NAV_LINKS = [
  { id: 'intro', label: 'Prelude' },
  { id: 'about', label: 'About' },
  { id: 'experience', label: 'Career' },
  { id: 'case-1', label: 'Work' },
  { id: 'process', label: 'Process' },
  { id: 'skills', label: 'Skills' },
  { id: 'contact', label: 'Contact' },
].map((l) => ({ ...l, idx: SECTIONS.findIndex((s) => s.id === l.id) }));

// Where each section sits along the page scroll (0..1). Desktop and mobile both
// use the cinematic horizontal scrub: maps to the point where the GSAP entrance
// of section i has completed (its timeline runs to ~N-0.5, entrance ends at i+0.5).
function sectionFraction(i) {
  const idx = Math.max(0, Math.min(N - 1, i));
  return idx <= 0 ? 0 : Math.min(1, (idx + 0.6) / (N - 0.5));
}

function nearestIndex(progress) {
  let best = 0;
  let bd = Infinity;
  for (let i = 0; i < N; i++) {
    const d = Math.abs(sectionFraction(i) - progress);
    if (d < bd) {
      bd = d;
      best = i;
    }
  }
  return best;
}

// rAF-driven smooth scroll to an exact target. Unlike native
// scroll-behavior:smooth, this is not cancelled when the initiating button
// gets disabled mid-flight, so endpoints (Intro / Contact) are always reached.
let scrollAnim;
function scrollToSection(i) {
  const max = document.documentElement.scrollHeight - window.innerHeight;
  const top = Math.round(sectionFraction(i) * max);
  cancelAnimationFrame(scrollAnim);
  const start = window.scrollY;
  const dist = top - start;
  if (Math.abs(dist) < 1) {
    window.scrollTo(0, top);
    return;
  }
  const dur = 620;
  const t0 = performance.now();
  const step = (now) => {
    const p = Math.min(1, (now - t0) / dur);
    const eased = 1 - Math.pow(1 - p, 3);
    window.scrollTo(0, start + dist * eased);
    if (p < 1) scrollAnim = requestAnimationFrame(step);
  };
  scrollAnim = requestAnimationFrame(step);
}

export default function Nav() {
  const [active, setActive] = useState(0);
  const raf = useRef();

  useEffect(() => {
    const loop = () => {
      const a = nearestIndex(scrollState.progress);
      setActive((p) => (p !== a ? a : p));
      raf.current = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(raf.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      const cur = nearestIndex(scrollState.progress);
      if (['ArrowDown', 'ArrowRight', 'PageDown'].includes(e.key)) {
        e.preventDefault();
        scrollToSection(cur + 1);
      } else if (['ArrowUp', 'ArrowLeft', 'PageUp'].includes(e.key)) {
        e.preventDefault();
        scrollToSection(cur - 1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        scrollToSection(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        scrollToSection(N - 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // MOBILE ONLY: discrete page-turn navigation. One swipe/wheel step advances
    // exactly one section and snaps to a full page — never resting mid-transition.
    // Tall sections still scroll internally; a page turn only fires once the
    // content has reached its scroll boundary in that direction.
    if (!window.matchMedia('(max-width: 767px)').matches) return undefined;

    const activeSection = () =>
      document.querySelectorAll('.overlay-section')[nearestIndex(scrollState.progress)] || null;

    const THRESH = 40;
    let startY = 0;
    let armed = false;
    let navLock = false;

    const boundaryBlocks = (sec, wantNext) => {
      if (!sec) return true;
      const overflowing = sec.scrollHeight > sec.clientHeight + 2;
      if (!overflowing) return true;
      const atTop = sec.scrollTop <= 0;
      const atBottom = sec.scrollTop + sec.clientHeight >= sec.scrollHeight - 1;
      return wantNext ? atBottom : atTop;
    };

    // Internal-scroll room in each direction, captured at gesture start.
    const boundaryState = (sec) => {
      if (!sec) return { canNext: false, canPrev: false };
      const overflowing = sec.scrollHeight > sec.clientHeight + 2;
      if (!overflowing) return { canNext: false, canPrev: false };
      return {
        canNext: sec.scrollTop + sec.clientHeight < sec.scrollHeight - 1,
        canPrev: sec.scrollTop > 0,
      };
    };

    let startState = { canNext: false, canPrev: false };

    const page = (dir) => {
      const cur = nearestIndex(scrollState.progress);
      const target = Math.max(0, Math.min(N - 1, cur + dir));
      if (target === cur) return;
      navLock = true;
      scrollToSection(target);
      setTimeout(() => { navLock = false; }, 700);
    };

    const onStart = (e) => {
      startY = e.touches[0].clientY;
      armed = false;
      startState = boundaryState(activeSection());
    };
    const onMove = (e) => {
      if (navLock) { e.preventDefault(); return; }
      const dy = startY - e.touches[0].clientY; // >0 => swipe up => next
      // Page only if the section had NO internal scroll room in this direction
      // at gesture start — reaching a boundary mid-swipe won't page until the
      // next swipe.
      const eligible = dy > 0 ? !startState.canNext : !startState.canPrev;
      if (eligible) {
        e.preventDefault();
        armed = true;
      }
    };
    const onEnd = (e) => {
      if (navLock || !armed) { armed = false; return; }
      const endY = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientY : startY;
      const dy = startY - endY;
      if (Math.abs(dy) > THRESH) page(dy > 0 ? 1 : -1);
      armed = false;
    };

    let wheelLock = false;
    const onWheel = (e) => {
      if (!boundaryBlocks(activeSection(), e.deltaY > 0)) return; // let internal scroll happen
      e.preventDefault();
      if (navLock || wheelLock || Math.abs(e.deltaY) < 6) return;
      wheelLock = true;
      page(e.deltaY > 0 ? 1 : -1);
      setTimeout(() => { wheelLock = false; }, 720);
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd, { passive: true });
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('wheel', onWheel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const activeLinkId =
    [...NAV_LINKS].reverse().find((l) => l.idx <= active)?.id || NAV_LINKS[0].id;

  const go = (dir) => scrollToSection(active + dir);

  return (
    <>
      {/* Edge arrows — sequential horizontal navigation */}
      <button
        data-testid="nav-prev"
        aria-label="Previous section"
        onClick={() => go(-1)}
        className="hidden md:grid fixed left-3 md:left-6 top-1/2 -translate-y-1/2 z-40 bg-ice text-white w-10 h-20 md:w-12 md:h-24 rounded-xl place-items-center hover:bg-[#0040a4] transition-colors disabled:opacity-30"
        disabled={active === 0}
      >
        <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2.5} />
      </button>
      <button
        data-testid="nav-next"
        aria-label="Next section"
        onClick={() => go(1)}
        className="hidden md:grid fixed right-3 md:right-6 top-1/2 -translate-y-1/2 z-40 bg-ice text-white w-10 h-20 md:w-12 md:h-24 rounded-xl place-items-center hover:bg-[#0040a4] transition-colors disabled:opacity-30"
        disabled={active === N - 1}
      >
        <ChevronRight className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2.5} />
      </button>

      {/* Bottom brand bar with section nav */}
      <div className="fixed bottom-0 inset-x-0 z-40 bg-ice text-white shadow-[0_-10px_40px_rgba(0,40,128,0.25)]">
        <div className="flex items-center justify-between px-5 md:px-10 h-14 md:h-16">
          <a
            href="#intro"
            data-testid="bottombar-wordmark"
            aria-label={PROFILE.name}
            onClick={(e) => {
              e.preventDefault();
              scrollToSection(0);
            }}
            className="logo-reveal"
          >
            {/* Compact LJ monogram — default, fades out on hover */}
            <img
              src="/logo-mark-t.png"
              alt=""
              data-testid="logo-mark"
              className="logo-reveal__mark"
            />
            {/* Words unveil left-to-right (L→INKO, J→ONOO) simultaneously */}
            <span className="logo-reveal__words" aria-hidden="true">
              <img src="/lj-linko.png" alt="" data-testid="logo-full" className="logo-reveal__part" />
              <img src="/lj-jonoo.png" alt="" className="logo-reveal__part" />
            </span>
          </a>
          <nav aria-label="Section navigation" className="hidden md:flex items-center gap-6 lg:gap-10">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => scrollToSection(l.idx)}
                aria-current={activeLinkId === l.id}
                className={`font-display text-sm lg:text-base tracking-wide transition-colors ${
                  activeLinkId === l.id ? 'text-white' : 'text-white/55 hover:text-white/90'
                }`}
              >
                {l.label}
              </button>
            ))}
          </nav>
          {/* Mobile: compact prev / indicator / next */}
          <div className="md:hidden flex items-center gap-3">
            <button
              data-testid="nav-prev-mobile"
              aria-label="Previous section"
              onClick={() => go(-1)}
              disabled={active === 0}
              className="grid place-items-center w-8 h-8 rounded-lg bg-white/15 text-white disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
            </button>
            <span data-testid="nav-indicator-mobile" className="text-xs tracking-widest2 text-white/80 tabular-nums">
              {String(active + 1).padStart(2, '0')} / {String(N).padStart(2, '0')}
            </span>
            <button
              data-testid="nav-next-mobile"
              aria-label="Next section"
              onClick={() => go(1)}
              disabled={active === N - 1}
              className="grid place-items-center w-8 h-8 rounded-lg bg-white/15 text-white disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
